import { Injectable } from '@angular/core';
import { ExecutionResult, LcsApiService } from '@lcs/shared/api';
import { OrderingOption } from 'generated/graphql-types';
import { Observable } from 'rxjs';
import { FilterExpression, FilterOption, serializeFilterExpression } from 'src/app/filters/filter-definition';
import { FilterOperations } from 'src/app/filters/filter-operation/filter-operations.enum';
import { FilterPrimitive } from 'src/app/filters/filter-primitives';
import { LogicalOperators } from 'src/app/filters/logical-operators.enum';
import { PhonesFilterOption } from '../models/phone-filter-option';
import { deletePhoneMutation } from '../queries/delete-phone.graphql';
import { DeletePhone, DeletePhoneVariables } from '../queries/generated/DeletePhone';
import { GetPhones, GetPhonesVariables } from '../queries/generated/GetPhones';
import { getPhonesQuery } from '../queries/phones.graphql';

@Injectable({ providedIn: 'root' })
export class PhoneRegisterApiService {
   constructor(private apiService: LcsApiService) {}

   getPhones(
      endCursor: string | null,
      filters: PhonesFilterOption[] | null,
      orderingOptions: OrderingOption[] | null
   ): Observable<ExecutionResult<GetPhones>> {
      const filterBuilderFilterExpression: FilterExpression | null =
         filters && filters.length > 0
            ? {
                 filterOptions: filters.map(this.convertPhoneFilterOption.bind(this)),
              }
            : null;
      const variables = this.getPhonesVariables(endCursor, filterBuilderFilterExpression, orderingOptions);
      return this.apiService.query<GetPhones, GetPhonesVariables>({
         query: getPhonesQuery,
         variables,
      });
   }

   deletePhone(phoneId: number): Observable<ExecutionResult<DeletePhone>> {
      return this.apiService.query<DeletePhone, DeletePhoneVariables>({
         query: deletePhoneMutation,
         variables: {
            id: phoneId,
         },
      });
   }

   private convertPhoneFilterOption(option: PhonesFilterOption): FilterOption {
      return {
         field: option.field,
         operation: option.operation,
         value: this.getFilterOptionValue(option),
      };
   }

   private getFilterOptionValue(option: PhonesFilterOption): FilterPrimitive | FilterPrimitive[] | null {
      if (option.operation === FilterOperations.HasValue) {
         return option.value;
      }
      switch (option.type) {
         case 'phone':
            return Array.isArray(option.value)
               ? option.value.map((v) => {
                    return v.phoneId;
                 })
               : option.value?.phoneId || null;
         case 'text-box':
         case 'date':
         case 'numeric':
         case 'boolean':
            return option.value;
      }
   }

   private getPhonesVariables(
      after: string | null,
      filterBuilderFilters: FilterExpression | null,
      orderingOptions: OrderingOption[] | null
   ): GetPhonesVariables {
      const variables: GetPhonesVariables = {
         after,
         filters: this.buildFilterExpression(filterBuilderFilters),
         first: 50,
         orderingOptions,
      };

      return variables;
   }

   private buildFilterExpression(filterBuilderFilters: FilterExpression | null): string {
      const expressions: FilterExpression[] = [];

      if (filterBuilderFilters) {
         return serializeFilterExpression({
            filterOptions: filterBuilderFilters.filterOptions,
            logicalOperator: LogicalOperators.And,
            subExpressions: expressions,
         });
      }

      if (expressions.length > 1) {
         return serializeFilterExpression({
            filterOptions: [],
            logicalOperator: LogicalOperators.And,
            subExpressions: expressions,
         });
      } else if (expressions.length === 1) {
         return serializeFilterExpression(expressions[0]);
      } else {
         return '';
      }
   }
}
